import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Folder, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { WalkRow, dropNote } from "./WalkRow";

// The walks already listened to, plus the folder of the ones worth keeping.
export const WalkHistory = ({ speaker, refreshKey }) => {
  const [walks, setWalks] = useState([]);
  const [filed, setFiled] = useState([]);
  const [keepDays, setKeepDays] = useState(90);
  const [notes, setNotes] = useState({});
  const [openFolder, setOpenFolder] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const unread = [...walks, ...filed].reduce((n, w) => n + (w.unread || 0), 0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/news/walks");
      setWalks(data.walks);
      setFiled(data.filed || []);
      setKeepDays(data.keep_days || 90);
    } catch (e) {
      console.error("could not load walks", e);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const replay = (walk) =>
    speaker.play(`walk-${walk.walk_id}`, async () =>
      (await api.get(`/news/walks/${walk.walk_id}`)).data.text);

  const seen = (walk_id) => {
    const clear = (rows) => rows.map((w) => (w.walk_id === walk_id ? { ...w, unread: 0 } : w));
    setWalks(clear);
    setFiled(clear);
  };

  const openNotes = async (walk) => {
    if (notes[walk.walk_id]) return setNotes((n) => ({ ...n, [walk.walk_id]: null }));
    try {
      const { data } = await api.get(`/news/walks/${walk.walk_id}/notes`);
      setNotes((n) => ({ ...n, [walk.walk_id]: data.notes }));
      seen(walk.walk_id);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not open the replies");
    }
  };

  // answering a friend's note in my own voice
  const reply = async (walk, blob) => {
    try {
      const fd = new FormData();
      fd.append("audio", blob, "note.webm");
      const { data } = await api.post(`/news/walks/${walk.walk_id}/notes`, fd,
                                     { headers: { "Content-Type": "multipart/form-data" } });
      setNotes((n) => ({ ...n, [walk.walk_id]: [...(n[walk.walk_id] || []), data] }));
      toast.success("Sent — your friend will see it on the walk you shared");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send that reply");
    }
  };

  const share = async (walk) => {
    try {
      const { data } = await api.post(`/news/walks/${walk.walk_id}/share`);
      navigator.clipboard?.writeText(`${window.location.origin}/news/walk/${data.token}`).catch(() => {});
      await load();
      toast.success("Link copied — your friend can listen to the same walk", { duration: 6000 });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not share that walk");
    }
  };

  const file = async (walk, into) => {
    try {
      await api.patch(`/news/walks/${walk.walk_id}/file?filed=${into}`);
      await load();
      if (into) setOpenFolder(true);
      toast.success(into ? "Saved in My Walks" : "Back in your recent walks");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not move that walk");
    }
  };

  const forget = async (walk) => {
    try {
      await api.delete(`/news/walks/${walk.walk_id}`);
      setWalks((rows) => rows.filter((w) => w.walk_id !== walk.walk_id));
      setFiled((rows) => rows.filter((w) => w.walk_id !== walk.walk_id));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not remove that walk");
    }
  };

  const rowProps = {
    speaker, onReplay: replay, onNotes: openNotes, onReply: reply, onShare: share,
    onFile: file, onForget: forget,
    onDropNote: async (walk, note) => {
      await dropNote(walk, note);
      setNotes((n) => ({ ...n,
        [walk.walk_id]: n[walk.walk_id].filter((x) => x.note_id !== note.note_id) }));
    },
  };

  if (walks.length === 0 && filed.length === 0) return null;

  return (
    <div className="mt-3" data-testid="walk-history">
      <p className="text-[11px] uppercase tracking-widest text-ink-soft">My walks</p>
      {unread > 0 && (
        <p className="mt-1.5 inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs font-semibold text-brand"
           data-testid="walk-replies-nudge">
          <MessageSquare className="h-3.5 w-3.5" />
          {unread} new {unread === 1 ? "reply" : "replies"} to your walks
        </p>
      )}

      <ul className="mt-1.5 space-y-1">
        {(showAll ? walks : walks.slice(0, 3)).map((walk) => (
          <WalkRow key={walk.walk_id} walk={walk} notes={notes[walk.walk_id]} {...rowProps} />
        ))}
      </ul>
      {walks.length > 3 && (
        <button
          data-testid="walks-show-all"
          onClick={() => setShowAll((s) => !s)}
          className="mt-1 text-[11px] text-ink-soft underline decoration-dotted hover:text-brand"
        >
          {showAll ? "Show fewer walks" : `Show all ${walks.length} walks`}
        </button>
      )}

      {filed.length > 0 && (
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3"
             data-testid="walk-folder">
          <button
            data-testid="walk-folder-toggle"
            onClick={() => setOpenFolder((o) => !o)}
            className="flex w-full items-center gap-2 text-xs font-semibold text-ink"
          >
            {openFolder ? <ChevronDown className="h-3.5 w-3.5 text-brand" />
                        : <ChevronRight className="h-3.5 w-3.5 text-brand" />}
            <Folder className="h-3.5 w-3.5 text-brand" /> My Walks
            <span className="text-ink-soft">· {filed.length} saved</span>
            <span className="ms-auto text-[11px] font-normal text-ink-soft">
              kept for {keepDays} days
            </span>
          </button>
          {openFolder && (
            <ul className="mt-2 space-y-1" data-testid="walk-folder-list">
              {filed.map((walk) => (
                <WalkRow key={walk.walk_id} walk={walk} notes={notes[walk.walk_id]} {...rowProps} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
